import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	computed,
} from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '@core/base.component';
import { generateXML } from '@core/util/eregistration';
import { createId, downloadFile, getFileFromBase64 } from '@core/util/file';
import { Tags } from '@core/util/tags';
import { MatterERegTab, MatterFileType, MatterTitle } from '@models/matter';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { select, Store } from '@ngxs/store';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UrlService } from '@services/url/url.service';
import { FileUploadState } from '@state/file-upload/fileUpload.state';
import { EregXmlSelectors } from '@state/global/ereg-xml';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { UploadFileStatus } from '@state/utils/models';
import { addDays, isAfter } from 'date-fns';
import { cloneDeep, orderBy } from 'lodash';
import LogRocket from 'logrocket';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	CALL_TO_ACTION_TYPES,
	NOTE_TYPES,
} from 'src/app/ui/note/note.component';
import { EregFileViewerComponent } from '../components/ereg-file-viewer/ereg-file-viewer.component';
import {
	ReviewTransferFieldsComponent,
	ReviewTransferFieldsComponentData,
} from '../components/review-transfer-fields/review-transfer-fields.component';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { deleteField } from '@angular/fire/firestore';
import { AppState } from '@state/app/app.state';
import { NavigationService } from '@services/nav/nav.service';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import {
	AnalyticsService,
	AnalyticsEvent,
} from '@services/analytics/analytics.service';
import { toSignal } from '@angular/core/rxjs-interop';

enum TransferPageTab {
	XML = 'xml',
	REGISTRATION = 'registration',
}

const tabs: ButtonGroupOption<TransferPageTab>[] = [
	{
		value: TransferPageTab.XML,
		label: 'Transfer XML',
		intercomAttr: 'transfer-xml-tab',
	},
	{
		value: TransferPageTab.REGISTRATION,
		label: 'Registration Details',
		intercomAttr: 'transfer-registration-details-tab',
	},
];

const NUM_OF_EREG_FIELDS = 18;

interface VM {
	disableCopyButton: boolean;
	eregPublicXmlUrl?: string;
	files?: {
		[key: string]: MatterFile;
	};
	selectedTab: TransferPageTab;
	systemTagPrefix?: string;
	transferDetails?: MatterERegTab;
	transferDocUrl?: string;
	canImportChargeXml?: boolean;
	missingFields: {
		message: string;
		action: () => void;
	}[];
}

const defaultVM: VM = {
	disableCopyButton: false,
	eregPublicXmlUrl: '',
	files: null,
	selectedTab: TransferPageTab.XML,
	systemTagPrefix: `${Tags.ADDITIONAL_REGISTER}:`,
	transferDetails: null,
	transferDocUrl: '',
	canImportChargeXml: false,
	missingFields: [],
};

@UntilDestroy()
@Component({
	selector: 'app-transfer',
	templateUrl: './transfer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TransferComponent extends BaseComponent<VM> implements OnInit {
	public AnalyticsEvent = AnalyticsEvent;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_ICON_CLASSES = AI_GENERATED_ICON.classes;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public BUTTON_PRESETS = BUTTON_PRESETS;
	public NUM_OF_EREG_FIELDS = NUM_OF_EREG_FIELDS;
	public MatterDocumentType = MatterDocumentType;
	public AdditionalRegisterTag = Tags.ADDITIONAL_REGISTER;
	public CallToActionType = CALL_TO_ACTION_TYPES;
	public NoteTypes = NOTE_TYPES;
	public teraviewLink =
		'https://www.teraviewereg.ca/TvApp/Login.jsp?language=en';

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) matterType: MatterFileType;
	@SelectSnapshot(SelectedMatter.closingDate) closingDate: string;
	@SelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];
	@SelectSnapshot(AppState.loadedFromLeap) loadedFromLeap: string;
	@SelectSnapshot(SelectedMatter.interestEstate) interestEstate: string;
	@SelectSnapshot(SelectedMatter.getTransferDocId) transferDocId: string;
	public isTPR_ = select(EregXmlSelectors.isTPR);
	public eregFile$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestERegTransfer,
	);
	public transferDetails$ = this.store.select(SelectedMatter.transferDetails);
	public transferInstrument$ = this.store.select(
		EregXmlSelectors.getTransferXml,
	);
	public fileStatusFilter$ = this.store.select(FileUploadState.getFileStatus);
	public allAdditionalRegisterFiles$ = this.store.select(
		MatterUploadedFilesSelectors.getAllAdditionalRegisterFiles,
	);
	public fieldMappings$ = this.store.select(SelectedMatter.getFieldMappings);
	public runMatterDocumentAiScrapings = toSignal(
		this.store.select(SelectedMatter.runMatterDocumentAiScrapings),
	);

	public firstSectionTitle_ = computed(() => {
		return `1. Review Transfer ${this.isTPR_() ? 'by Personal Rep' : ''} XML`;
	});
	public secondSectionTitle_ = computed(() => {
		return `2. Import Transfer ${this.isTPR_() ? 'by Personal Rep' : ''} XML`;
	});

	public tabs = tabs;
	public TransferPageTab = TransferPageTab;
	public transferXml;
	public transferObject;
	public enableXmlDownload = false;
	public ICON = ICON;

	constructor(
		private matterDispatch: MatterDispatch,
		private drawerService: DrawerService,
		private snackbarService: SnackbarService,
		private router: Router,
		private clipboard: ClipboardService,
		private store: Store,
		private navService: NavigationService,
		private analytics: AnalyticsService,
	) {
		super(defaultVM);
	}

	public ngOnInit(): void {
		const transferDocUrl = UrlService.Matter_Documents_Document(
			this.matterId,
			this.transferDocId,
		);

		const closingDate = new Date(this.closingDate);
		const currentDate = new Date();
		const dayAfterClosingDate = addDays(closingDate, 1);
		this.VM = {
			selectedTab:
				this.closingDate && isAfter(currentDate, dayAfterClosingDate)
					? TransferPageTab.REGISTRATION
					: TransferPageTab.XML,
			transferDocUrl,
		};

		combineLatest([
			this.transferDetails$,
			this.allAdditionalRegisterFiles$,
			this.store.select(SelectedMatter.getTransferEregFieldMappings),
		])
			.pipe(untilDestroyed(this))
			.subscribe(([transferDetails, files, additionalRegisterMappings]) => {
				const additionalRegisterKeys = Object.keys(
					transferDetails?.additionalRegisters ?? {},
				);

				const newFiles = {};

				additionalRegisterKeys.forEach((key) => {
					const file = this.getLatestAdditionalRegisterFileById(key, files);

					if (file) {
						newFiles[key] = {
							...file,
							additionalRegisterMappings:
								additionalRegisterMappings?.[key] ?? {},
						};
					}
				});

				this.VM = {
					transferDetails,
					files: newFiles,
				};
			});

		this.transferInstrument$
			.pipe(untilDestroyed(this))
			.subscribe((transfer) => {
				this.transferXml = generateXML(transfer);
				if (!this.transferObject) {
					this.transferObject = cloneDeep(transfer);
				}

				this.VM = {
					canImportChargeXml:
						this.transferObject?.Instrument?.Amount &&
						this.matterTitles?.length > 0,
					missingFields: this.getMissingFields(),
				};
				this.enableXmlDownload = true;
			});
	}

	download() {
		downloadFile(this.transferObject.Instrument.Name, this.transferXml, 'xml');
	}

	copyXMLToClipboard() {
		if (this.VM.eregPublicXmlUrl) {
			this.clipboard.copyText(this.VM.eregPublicXmlUrl, {
				success: {
					text: 'The XML file URL has been copied to your clipboard.',
					type: 'success',
				},
			});
		} else {
			this.createXMLUrl();
		}
	}

	onRegistrationNumberChange(registrationNumber: string): void {
		this.matterDispatch.OverwriteFields(
			'ereg.transfer.registrationNumber',
			registrationNumber,
		);
	}

	onRegistrationDateChange(registrationDate: string): void {
		this.matterDispatch.OverwriteFields(
			'ereg.transfer.registrationDate',
			registrationDate,
		);
	}

	getLatestAdditionalRegisterFileById(
		id: string,
		files: MatterFile[],
	): MatterFile {
		const _filteredFiles = files?.filter((x) =>
			x.systemTags.includes(`${Tags.ADDITIONAL_REGISTER}:${id}`),
		);

		const ordered = orderBy(_filteredFiles, 'uploadedAt', ['desc']);

		return ordered?.length > 0 ? ordered[0] : null;
	}

	viewFile(file: MatterFile) {
		this.drawerService.openDrawer(EregFileViewerComponent, {
			title: file.name,
			noStyle: true,
			data: {
				file,
			},
		});
	}

	addAdditionalRegister(): void {
		this.matterDispatch.OverwriteFields(
			`ereg.transfer.additionalRegisters.${createId()}`,
			{
				name: '',
				registrationNumber: '',
				registrationDate: '',
			},
		);
	}

	removeAdditionalRegister(uid: string): void {
		this.matterDispatch.OverwriteFields(
			`ereg.transfer.additionalRegisters.${uid}`,
			deleteField(),
		);
	}

	updateAdditionalRegisterName(name: string, uid: string): void {
		this.matterDispatch.OverwriteFields(
			`ereg.transfer.additionalRegisters.${uid}.name`,
			name,
		);
	}

	updateAdditionalRegisterRegistrationNumber(
		registrationNumber: string,
		uid: string,
	): void {
		this.matterDispatch.OverwriteFields(
			`ereg.transfer.additionalRegisters.${uid}.registrationNumber`,
			registrationNumber,
		);
	}

	updateAdditionalRegisterRegistrationDate(
		registrationDate: string,
		uid: string,
	): void {
		this.matterDispatch.OverwriteFields(
			`ereg.transfer.additionalRegisters.${uid}.registrationDate`,
			registrationDate,
		);
	}

	openXMLDetails() {
		this.drawerService.openDrawer(ReviewTransferFieldsComponent, {
			title: 'Review Transfer XML Details',
			data: {
				transferObject: this.transferObject,
				matterId: this.matterId,
				matterType: this.matterType,
				interestEstate: this.interestEstate,
				matterTitles: this.matterTitles,
				missingFields: this.VM.missingFields,
			} as ReviewTransferFieldsComponentData,
			size: 'sm',
		});
	}

	createXMLUrl() {
		if (!this.VM.disableCopyButton) {
			const file = getFileFromBase64(
				window.btoa(this.transferXml),
				this.transferObject.Instrument.Name,
				'text/xml',
			);
			const docId = createId();

			this.matterDispatch.UploadEregXMLFile(docId, this.matterId, file);

			this.getFileStatus(docId)
				.pipe(untilDestroyed(this))
				.subscribe((status) => {
					if (status.status === UploadFileStatus.SUCCESS) {
						this.snackbarService.openSnackBar({
							text: 'New XML file URL successfully created',
							type: 'success',
						});
						this.VM = {
							eregPublicXmlUrl: status.publicUrl,
						};
						LogRocket.info('Teraview public url', status.publicUrl);
						this.clipboard.copyText(this.VM.eregPublicXmlUrl, {
							success: {
								text: 'The XML file URL has been copied to your clipboard.',
								type: 'success',
							},
						});
					}
				});
		}
	}

	getFileStatus(id: string) {
		return this.fileStatusFilter$.pipe(
			untilDestroyed(this),
			map((func) => func(id)),
		);
	}

	handleViewNowBtnClick() {
		if (this.loadedFromLeap) {
			// FT: This is wrong so fix it during cleanup
			// It should redirect to Documents_SaleDocs only if the business is using HTML Document, to URL/Flow for word document would be different
			this.navService.goToMatter_Documents_SaleDocs(this.matterId);
		} else {
			this.router.navigate([this.VM.transferDocUrl]);
		}
	}

	trackByFn(_: number, item: MatterERegTab['additionalRegisters']) {
		return item.key;
	}

	getMissingFields(): { message: string; field: string; action: () => void }[] {
		const missingFields = [];

		if (
			!this.matterTitles?.length ||
			this.matterTitles.some((t) => !t.pinNumber)
		) {
			missingFields.push({
				message: 'PIN required to create Teraview instrument.',
				action: () => {
					this.navService.goToMatter_Property(this.matterId, {});
				},
			});
		}

		if (!this.transferObject?.Instrument?.Amount) {
			missingFields.push({
				message: 'Sale Price required to create Teraview instrument.',
				action: () => {
					this.navService.goToMatter_Adjustments(this.matterId);
				},
			});
		}

		return missingFields;
	}

	onEregTransferFileSuccess() {
		this.analytics.logEvent(
			this.runMatterDocumentAiScrapings()?.[MatterDocumentType.EREG_TRANSFER]
				? AnalyticsEvent.EREG_TRANSFER_AI_ON
				: AnalyticsEvent.EREG_TRANSFER_AI_OFF,
		);
	}
}
